/**
 * PersonaSlicerDialog (doc 147 §6.4 + PR Q3 writable presets + drill-down).
 *
 * Stacks on top of ConstellationFilters: pick a preset, see the live
 * `slicerBreakdown` of what the persona hides. PR Q3 adds:
 *  - Writable user presets (Save as new / Edit / Delete) via
 *    customPersonas localStorage.
 *  - Per-domain drill-down showing the actual hidden entities (was
 *    just a count). Click a domain row → DrillDownPanel.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AtlasManifest, DomainLayer } from '@atlas-manifest/types';
import {
  PERSONA_PRESETS,
  slicerBreakdown,
  type PersonaPreset,
} from '../state/personaPresets.js';
import { defaultFilters, applyFilters, type ConstellationFilters } from '../state/filters.js';
import {
  readCustomPersonas,
  saveCustomPersona,
  deleteCustomPersona,
  type CustomPersona,
} from '../state/customPersonas.js';
import { computeDomainStats } from '../state/selectors.js';
import { Icon } from '../components/Icon.js';
import { ErrorState } from '../components/ErrorState.js';
import { useInlineError, persistErrorMessage } from '../state/useInlineError.js';
import { DrillDownPanel } from '../components/DrillDownPanel.js';
import { EntityChip } from '../components/EntityChip.js';
import { DomainBadge } from './DomainBadge.js';
import { LayerBadge } from './LayerBadge.js';
import { ConnectivityPill } from './ConnectivityPill.js';
import { useFocusTrap } from '../state/useFocusTrap.js';

/** R1.M2 — letters, numbers, whitespace, hyphens, underscores, dots; 1–64 chars. */
const VALID_LABEL = /^[\p{L}\p{N}\s\-_.]{1,64}$/u;

export interface PersonaSlicerDialogProps {
  manifest: AtlasManifest;
  filters: ConstellationFilters;
  onChange: (next: ConstellationFilters) => void;
  onClose: () => void;
}

export function PersonaSlicerDialog({
  manifest,
  filters,
  onChange,
  onClose,
}: PersonaSlicerDialogProps): React.JSX.Element {
  const [customPersonas, setCustomPersonas] = useState<CustomPersona[]>(() => readCustomPersonas());
  const [drillDomain, setDrillDomain] = useState<string | null>(null);
  const [error, showError, clearError] = useInlineError();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useFocusTrap(panelRef, true);

  // Move focus into the dialog on open — unlike SearchPalette/EntityCommandPalette
  // (which autofocus their search input), this dialog has no input to autofocus,
  // so without this the focus trap above has nothing inside itself to constrain
  // until the user happens to tab into the panel on their own.
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const applyPreset = (preset: PersonaPreset | CustomPersona): void => {
    onChange({ ...filters, ...preset.filters });
  };

  const breakdown = useMemo(() => slicerBreakdown(manifest, filters), [manifest, filters]);
  const statsMap = useMemo(() => {
    const stats = computeDomainStats(manifest);
    const map = new Map(stats.map((s) => [s.domainId, s]));
    return map;
  }, [manifest]);

  const saveAsNew = (): void => {
    const raw = window.prompt('Persona name')?.trim();
    if (!raw) return;
    if (!VALID_LABEL.test(raw)) {
      showError('Name must be 1–64 chars: letters, numbers, spaces, hyphens, underscores, dots.');
      return;
    }
    const id = `custom:${raw.toLowerCase().replace(/\s+/g, '-')}-${Date.now().toString(36)}`;
    const p: CustomPersona = {
      id,
      label: raw,
      description: 'Custom persona saved from current filters.',
      filters: {
        domain: filters.domain,
        tier: filters.tier,
        pack: filters.pack,
        isMinor: filters.isMinor,
      },
    };
    const result = saveCustomPersona(p);
    if (!result.ok) { showError(persistErrorMessage(result, 'save persona')); return; }
    setCustomPersonas(readCustomPersonas());
  };

  const deletePersona = (id: string): void => {
    const result = deleteCustomPersona(id);
    if (!result.ok) { showError(persistErrorMessage(result, 'delete persona')); return; }
    setCustomPersonas(readCustomPersonas());
  };

  const renamePersona = (p: CustomPersona): void => {
    const raw = window.prompt('Persona name', p.label)?.trim();
    if (!raw || raw === p.label) return;
    if (!VALID_LABEL.test(raw)) {
      showError('Name must be 1–64 chars: letters, numbers, spaces, hyphens, underscores, dots.');
      return;
    }
    const result = saveCustomPersona({ ...p, label: raw });
    if (!result.ok) { showError(persistErrorMessage(result, 'rename persona')); return; }
    setCustomPersonas(readCustomPersonas());
  };

  // Hidden entities per domain (for drill).
  const hiddenByDomain = useMemo(() => {
    const visible = applyFilters(manifest, filters);
    const out = new Map<string, string[]>();
    for (const e of manifest.entities) {
      if (visible.has(e.id)) continue;
      const list = out.get(e.domain) ?? [];
      list.push(e.id);
      out.set(e.domain, list);
    }
    return out;
  }, [manifest, filters]);

  const drillEntities = drillDomain ? hiddenByDomain.get(drillDomain) ?? [] : [];

  return (
    <div
      className="persona-slicer-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Persona slicer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={panelRef} className="persona-slicer-panel" onClick={(e) => e.stopPropagation()}>
        <header className="persona-slicer-head">
          <h2>Persona slicer</h2>
          <button ref={closeButtonRef} className="btn btn-ghost" onClick={onClose} aria-label="Close persona slicer">
            <Icon name="close" size="md" />
          </button>
        </header>

        {error && <ErrorState message={error} onDismiss={clearError} />}

        <section aria-label="Built-in presets" className="persona-slicer-presets">
          <h3 className="persona-presets-head">Built-in</h3>
          {PERSONA_PRESETS.map((preset) => {
            const active = matchesPreset(filters, preset);
            return (
              <button
                key={preset.id}
                className={`persona-preset${active ? ' is-active' : ''}`}
                aria-pressed={active}
                onClick={() => applyPreset(preset)}
              >
                <div className="persona-preset-label">{preset.label}</div>
                <div className="persona-preset-desc">{preset.description}</div>
                <span className="persona-preset-tag">built-in</span>
              </button>
            );
          })}
        </section>

        {customPersonas.length > 0 && (
          <section aria-label="Custom presets" className="persona-slicer-presets">
            <h3 className="persona-presets-head">Custom · you</h3>
            {customPersonas.map((p) => {
              const active = matchesPreset(filters, p);
              return (
                <div key={p.id} className="persona-preset-wrap">
                  <button
                    className={`persona-preset${active ? ' is-active' : ''}`}
                    aria-pressed={active}
                    onClick={() => applyPreset(p)}
                  >
                    <div className="persona-preset-label">{p.label}</div>
                    <div className="persona-preset-desc">{p.description}</div>
                    <span className="persona-preset-tag custom">custom</span>
                  </button>
                  <div className="persona-preset-actions">
                    <button className="btn btn-ghost" onClick={() => renamePersona(p)} aria-label={`Rename ${p.label}`}>
                      <Icon name="edit" size="sm" />
                    </button>
                    <button className="btn btn-ghost" onClick={() => deletePersona(p.id)} aria-label={`Delete ${p.label}`}>
                      <Icon name="delete" size="sm" />
                    </button>
                  </div>
                </div>
              );
            })}
          </section>
        )}

        <section aria-label="Persona actions" className="persona-slicer-saveas">
          <button className="btn btn-primary" onClick={saveAsNew}>
            <Icon name="bookmark_add" size="sm" />
            Save current as new persona
          </button>
        </section>

        <section aria-label="What's hidden" className="persona-slicer-breakdown">
          <div className="breakdown-row">
            <span className="k">Entities</span>
            <b>{breakdown.entitiesVisible} visible · {breakdown.entitiesHidden} hidden</b>
          </div>
          <div className="breakdown-row">
            <span className="k">Surfaces</span>
            <b>{breakdown.surfacesVisible} visible · {breakdown.surfacesHidden} hidden</b>
          </div>
          {breakdown.perDomain.length > 0 && (
            <div className="breakdown-domains">
              <h4 className="breakdown-domains-head">Per-domain breakdown</h4>
              <table className="breakdown-domain-table">
                <thead>
                  <tr>
                    <th scope="col">Domain</th>
                    <th scope="col">Visible</th>
                    <th scope="col">Hidden</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.perDomain.map((d) => {
                    const stats = statsMap.get(d.domainId);
                    const domain = manifest.domains.find((dom) => dom.id === d.domainId);
                    return (
                      <tr key={d.domainId}>
                        <td className="mono" style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
                          <DomainBadge domainId={d.domainId} size="sm" />
                          {domain && <LayerBadge layer={(domain.layer as DomainLayer) ?? 'L5'} size="sm" />}
                          {stats && <ConnectivityPill status={stats.connectivity} />}
                        </td>
                        <td><span className="chip good">{d.visible}</span></td>
                        <td>
                          {d.hidden > 0 ? (
                            <button
                              type="button"
                              className="chip warn breakdown-hidden-btn"
                              onClick={() => setDrillDomain(d.domainId)}
                              aria-label={`Show ${d.hidden} hidden entities in ${d.domainName}`}
                            >
                              {d.hidden} →
                            </button>
                          ) : (
                            <span className="chip">0</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {breakdown.warnings.length > 0 && (
          <section aria-label="Warnings" className="persona-slicer-warnings">
            {breakdown.warnings.map((w) => (
              <div key={w} className="persona-slicer-warning">
                <Icon name="warning" size="sm" />
                <span>{w}</span>
              </div>
            ))}
          </section>
        )}

        <footer className="persona-slicer-footer">
          <button className="btn btn-ghost" onClick={() => onChange(defaultFilters)}>
            Reset filters
          </button>
        </footer>

        <DrillDownPanel
          open={drillDomain !== null}
          title={drillDomain ? `Hidden in ${drillDomain}` : ''}
          subtitle={`${drillEntities.length} ${drillEntities.length === 1 ? 'entity' : 'entities'} hidden by current persona`}
          onClose={() => setDrillDomain(null)}
        >
          {drillDomain && (
            <div className="persona-drill-entities">
              {drillEntities.length === 0 ? (
                <p className="persona-drill-empty">No entities hidden in this domain.</p>
              ) : (
                drillEntities.map((id) => (
                  <EntityChip key={id} entityId={id} manifest={manifest} />
                ))
              )}
            </div>
          )}
        </DrillDownPanel>
      </div>
    </div>
  );
}

/** True when the current filters match the preset's filter patch exactly. */
function matchesPreset(
  filters: ConstellationFilters,
  preset: PersonaPreset | CustomPersona,
): boolean {
  for (const [key, value] of Object.entries(preset.filters)) {
    if ((filters as unknown as Record<string, unknown>)[key] !== value) return false;
  }
  return true;
}
